"use strict";
/*******************************************************************************

    Contains a class for watching new blocks and new transactions of account

    Copyright:
        Copyright (c) 2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WalletWatcher = void 0;
var EventDispatcher_1 = require("./EventDispatcher");
var Types_1 = require("./Types");
var jsbi_1 = __importDefault(require("jsbi"));
var socket_io_client_1 = require("socket.io-client");
/**
 * It monitors whether new blocks has been created.
 * It monitors whether transactions have occurred in accounts registered in Wallet in real time.
 */
var WalletWatcher = /** @class */ (function (_super) {
    __extends(WalletWatcher, _super);
    /**
     * Constructor
     * @param accounts  The account container of the wallet
     * @param client    The instance of WalletClient
     * @param duration  The milliseconds that pulling data.
     */
    function WalletWatcher(accounts, client, duration) {
        if (duration === void 0) { duration = 10000; }
        var _this = _super.call(this) || this;
        _this.height = 0;
        _this.accounts = accounts;
        _this.client = client;
        _this.pullingTimer = undefined;
        _this.pingTimer = undefined;
        _this.duration = duration;
        _this.socket = socket_io_client_1.io(_this.client.getEndpoint().stoa);
        return _this;
    }
    /**
     * It is the tasks to be done at the beginning of the creation of this class. This is called from the constructor.
     */
    WalletWatcher.prototype.initialize = function () {
        this.socket.on("new_tx_acc", this.onNewTransaction.bind(this));
        for (var _i = 0, _a = this.accounts.items; _i < _a.length; _i++) {
            var account = _a[_i];
            this.subscribe(account.address);
        }
        this.accounts.addEventListener(EventDispatcher_1.Event.ADDED, this.onChangedAccount, this);
        this.accounts.addEventListener(EventDispatcher_1.Event.REMOVED, this.onChangedAccount, this);
        this.startPulling();
        if (this.pingTimer !== undefined)
            clearInterval(this.pingTimer);
        this.pingTimer = setInterval(this.ping.bind(this), 30000);
    };
    /**
     * It is the tasks to be done before they are destroyed. It should be called when an instance of it is no longer needed.
     */
    WalletWatcher.prototype.finalize = function () {
        if (this.pingTimer !== undefined)
            clearInterval(this.pingTimer);
        this.accounts.removeEventListener(EventDispatcher_1.Event.ADDED, this.onChangedAccount, this);
        this.accounts.removeEventListener(EventDispatcher_1.Event.REMOVED, this.onChangedAccount, this);
        for (var _i = 0, _a = this.accounts.items; _i < _a.length; _i++) {
            var account = _a[_i];
            this.unsubscribe(account.address);
        }
        this.socket.disconnect();
        this.stopPulling();
    };
    /**
     * This is called when Wallet's account is added or deleted.
     */
    WalletWatcher.prototype.onChangedAccount = function (type, account) {
        if (type === EventDispatcher_1.Event.ADDED) {
            this.subscribe(account.address);
        }
        else if (type === EventDispatcher_1.Event.REMOVED) {
            this.unsubscribe(account.address);
        }
    };
    /**
     * Subscribe to Stoa to monitor new transactions.
     * @param address The address to monitor.
     */
    WalletWatcher.prototype.subscribe = function (address) {
        this.socket.emit("subscribe", { address: address.toString() });
    };
    /**
     * Unsubscribed to Stoa to avoid monitoring new transactions
     * @param address The address to monitor.
     */
    WalletWatcher.prototype.unsubscribe = function (address) {
        this.socket.emit("unsubscribe", { address: address.toString() });
    };
    /**
     * It is a message handler that delivers a new transaction occurs on the server.
     * @param data { address: string; tx_hash: string; type: string  }
     * @private
     */
    WalletWatcher.prototype.onNewTransaction = function (data) {
        if (data === undefined || data.address === undefined)
            return;
        var account = this.accounts.find(data.address);
        if (account !== undefined) {
            account.checkBalance();
            this.dispatchEvent(EventDispatcher_1.Event.NEW_TRANSACTION, data);
        }
    };
    /**
     * Start pulling the block height
     */
    WalletWatcher.prototype.startPulling = function () {
        if (this.pullingTimer !== undefined)
            clearInterval(this.pullingTimer);
        this.pullingTimer = setInterval(this.pulling.bind(this), this.duration);
    };
    /**
     * Periodically pull in case of failure in real-time notification.
     */
    WalletWatcher.prototype.pulling = function () {
        return __awaiter(this, void 0, void 0, function () {
            var res, height, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.client.getBlockHeight()];
                    case 1:
                        res = _a.sent();
                        if (res.code === Types_1.WalletResultCode.Success && res.data !== undefined) {
                            height = jsbi_1.default.toNumber(res.data);
                            if (height > this.height) {
                                this.height = height;
                                this.accounts.checkBalance(false);
                                this.dispatchEvent(EventDispatcher_1.Event.NEW_BLOCK, this.height);
                            }
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _a.sent();
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Stop pulling the block height
     */
    WalletWatcher.prototype.stopPulling = function () {
        if (this.pullingTimer !== undefined)
            clearInterval(this.pullingTimer);
        this.pullingTimer = undefined;
    };
    /**
     * Periodically notify the server that the client is alive.
     * The server forcibly shuts down if it does not reach for one minute.
     */
    WalletWatcher.prototype.ping = function () {
        try {
            this.socket.emit("ping", "");
        }
        catch (e) {
            this.reconnect();
        }
    };
    /**
     * When the connection is terminated, the connection is re-connected.
     */
    WalletWatcher.prototype.reconnect = function () {
        var _this = this;
        try {
            this.socket.disconnect();
        }
        catch (e) {
            //
        }
        this.socket = socket_io_client_1.io(this.client.getEndpoint().stoa);
        this.socket.on("connect", (function () {
            _this.initialize();
        }).bind(this));
    };
    return WalletWatcher;
}(EventDispatcher_1.EventDispatcher));
exports.WalletWatcher = WalletWatcher;
//# sourceMappingURL=WalletWatcher.js.map